"use client"

import { cn } from "@/shared/lib/utils"
import Link from "next/link"
import type React from "react"
import { useEffect, useId, useMemo, useRef } from "react"

export type LogoVariant = "default" | "light"

type Props = {
    className?: string
    size?: "sm" | "md" | "lg"
    link?: string
    label?: string
    variant?: LogoVariant
    id?: string
}

export function ZentjesLogo({ className, size = "md", link, label = "Zentjes", variant = "default", id }: Props) {
    const sizes = {
        sm: "h-8 w-8",
        md: "h-12 w-12",
        lg: "h-16 w-16",
    } as const

    const reactId = useId().replace(/:/g, "")
    const uid = id || reactId
    const ids = useMemo(() => {
        return {
            mask: `z-mask-${uid}`,
            grad: `z-gradient-${uid}`,
            shim: `z-shimmer-${uid}`,
            burst: `z-burst-${uid}`,
            glow: `z-glow-${uid}`,
            title: `z-title-${uid}`,
            desc: `z-desc-${uid}`,
        }
    }, [uid])

    const shim1Ref = useRef<SVGAnimateElement>(null)
    const shim2Ref = useRef<SVGAnimateElement>(null)
    const hoverAnimRef = useRef<SVGAnimateElement>(null)

    const zPath = "M7 9 L25 9 L18 17 M14 15 L7 23 L25 23"

    useEffect(() => {
        const timer = setTimeout(() => {
            runLoadShimmer()
        }, 1000)
        return () => clearTimeout(timer)
    }, [])

    function runLoadShimmer() {
        const a1 = shim1Ref.current
        if (!a1) return

        a1.setAttribute("from", "-32")
        a1.setAttribute("to", "64")
        a1.setAttribute("dur", "3.5s")
        a1.beginElement()
    }

    function runShimmer(fromLeft: boolean) {
        const a1 = shim1Ref.current
        const a2 = shim2Ref.current
        const a3 = hoverAnimRef.current
        if (!a1 || !a2 || !a3) return

        a1.setAttribute("dur", "2.5s")

        if (fromLeft) {
            a1.setAttribute("from", "-32")
            a1.setAttribute("to", "64")
            a2.setAttribute("from", "0")
            a2.setAttribute("to", "96")
            a3.setAttribute("from", "-32")
            a3.setAttribute("to", "32")
        } else {
            a1.setAttribute("from", "32")
            a1.setAttribute("to", "-64")
            a2.setAttribute("from", "8")
            a2.setAttribute("to", "-64")
            a3.setAttribute("from", "32")
            a3.setAttribute("to", "-32")
        }

        a1.beginElement()
        a2.beginElement()
        a3.beginElement()
    }

    function onEnter(e: React.PointerEvent<HTMLDivElement>) {
        if (e.pointerType === "touch") return
        const rect = e.currentTarget.getBoundingClientRect()
        const x = e.clientX - rect.left
        runShimmer(x < rect.width / 2)
        // Removed separate hoverAnimRef call as it's now in runShimmer
    }

    function onMove(e: React.PointerEvent<HTMLDivElement>) {
        if (e.pointerType === "touch") return
        const rect = e.currentTarget.getBoundingClientRect()
        const x = e.clientX - rect.left
        runShimmer(x < rect.width / 2)
    }

    function onFocus() {
        runShimmer(true)
        // Removed separate hoverAnimRef call as it's now in runShimmer
    }

    const Svg = (
        <svg
            viewBox="0 0 32 32"
            fill="none"
            className="h-[65%] w-[65%] relative z-10"
            style={{ overflow: "hidden" }}
            role="img"
            aria-labelledby={`${ids.title} ${ids.desc}`}
            focusable="false"
        >
            <title id={ids.title}>{label}</title>
            <desc id={ids.desc}>Stylized letter Z logo</desc>

            <defs>
                <mask id={ids.mask}>
                    <rect x="0" y="0" width="32" height="32" fill="black" />
                    <path d={zPath} stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </mask>

                {/* Main Gradient - Switches based on variant */}
                <linearGradient id={ids.grad} x1="0%" y1="0%" x2="100%" y2="100%">
                    {variant === "light" ? (
                        <>
                            <stop offset="0%" stopColor="#57534e" />
                            <stop offset="50%" stopColor="#78716c" />
                            <stop offset="100%" stopColor="#44403c" />
                        </>
                    ) : (
                        <>
                            <stop offset="0%" stopColor="#a8a29e" />
                            <stop offset="50%" stopColor="#d6d3d1" />
                            <stop offset="100%" stopColor="#78716c" />
                        </>
                    )}
                </linearGradient>

                {/* Mirror Force shimmer - golden/warm tones */}
                <linearGradient id={ids.shim} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="transparent" stopOpacity="0" />
                    <stop offset="30%" stopColor="#fcd34d" stopOpacity="0.15" />
                    <stop offset="50%" stopColor="#fef3c7" stopOpacity="0.4" />
                    <stop offset="70%" stopColor="#fcd34d" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </linearGradient>

                {/* Secondary burst shimmer - bright white core */}
                <linearGradient id={ids.burst} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="transparent" stopOpacity="0" />
                    <stop offset="40%" stopColor="#fff" stopOpacity="0.2" />
                    <stop offset="50%" stopColor="#fff" stopOpacity="0.35" />
                    <stop offset="60%" stopColor="#fff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </linearGradient>

                {/* Glow filter for burst effect */}
                <filter id={ids.glow} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            <path
                d={zPath}
                stroke={`url(#${ids.grad})`}
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                className={cn(
                    "drop-shadow-sm transition-all duration-300",
                    variant === "light"
                        ? "group-hover:drop-shadow-[0_0_8px_rgba(0,0,0,0.2)]"
                        : "group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
                )}
            />

            {/* Inner glow/highlight - only for dark mode or adjusted for light */}
            {variant !== "light" && (
                <path
                    d={zPath}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    style={{ filter: "blur(0.5px)" }}
                />
            )}

            {/* Primary golden shimmer - slow sweep */}
            <rect
                x="-32"
                y="0"
                width="32"
                height="32"
                fill={`url(#${ids.shim})`}
                mask={`url(#${ids.mask})`}
                style={{ mixBlendMode: "screen" }}
            >
                <animate
                    ref={shim1Ref}
                    attributeName="x"
                    from="-32"
                    to="32"
                    dur="2.5s"
                    begin="indefinite"
                    fill="freeze"
                    calcMode="spline"
                    keySplines="0.4 0 0.2 1"
                    keyTimes="0;1"
                />
            </rect>

            {/* White burst shimmer - fast explosive pass */}
            <g mask={`url(#${ids.mask})`} style={{ mixBlendMode: "overlay" }}>
                <rect
                    x="-32"
                    y="0"
                    width="24"
                    height="32"
                    fill={`url(#${ids.burst})`}
                    filter={`url(#${ids.glow})`}
                >
                    <animate
                        ref={shim2Ref}
                        attributeName="x"
                        from="-32"
                        to="32"
                        dur="1.2s"
                        begin="indefinite"
                        fill="freeze"
                        calcMode="spline"
                        keySplines="0.2 0 0 1"
                        keyTimes="0;1"
                    />
                </rect>
            </g>

            {/* Hover shimmer - triggered manually */}
            <rect
                x="-64"
                y="0"
                width="32"
                height="32"
                fill={`url(#${ids.shim})`}
                mask={`url(#${ids.mask})`}
                style={{ mixBlendMode: "screen" }}
            >
                <animate
                    ref={hoverAnimRef}
                    attributeName="x"
                    from="-32"
                    to="32"
                    dur="1.5s"
                    begin="indefinite"
                    repeatCount="1"
                    fill="freeze"
                />
            </rect>
        </svg>
    )

    const Base = (
        <div
            className={cn(
                "relative flex items-center justify-center rounded-xl overflow-hidden cursor-default group outline-none",
                variant === "light"
                    ? "bg-gradient-to-br from-white via-stone-50 to-stone-100 ring-1 ring-stone-200 shadow-lg shadow-stone-200/50"
                    : "bg-gradient-to-br from-stone-900 via-stone-950 to-black ring-1 ring-stone-800/50 shadow-lg shadow-stone-950/50",
                sizes[size],
                className
            )}
            onPointerEnter={onEnter}
            onFocus={onFocus}
        >
            {Svg}
        </div>
    )

    if (!link) return Base

    return (
        <Link
            href={link}
            aria-label={label}
            className="inline-flex outline-none focus:outline-none focus-visible:outline-none"
            onFocus={onFocus}
            onPointerEnter={(e) => onEnter(e as unknown as React.PointerEvent<HTMLDivElement>)}
        >
            {Base}
        </Link>
    )
}